import Image from 'next/image';
import type React from 'react';
import {
  BOX_PAGE_ADVISER_DETAIL,
  BOX_PAGE_QUESTION_DETAIL,
} from '../../../core/entities/box/constants';
import type {QuestionWithAnswersType} from '../../../core/repositories/boxRepositoryTypes';
import {CommonLink} from '../../../shared/components/CommonLink';
import {convertNlToBr} from '../../../shared/services/convertNlToBr';
import {formatDate} from '../../../shared/services/formatDate';
import {createBoxPath} from '../usecases/routes';
import {useAnswerLiked} from '../usecases/useAnswerLiked';
import {LikeButton} from './LikeButton';
import {ShareButton} from './ShareButton';

type QuestionProps = {
  question: QuestionWithAnswersType;
};

export const QuestionFeedItem: React.FC<QuestionProps> = ({question}) => {
  const answer = question.answers[0]!;
  const QuestionDetailURL = createBoxPath({
    page: BOX_PAGE_QUESTION_DETAIL,
    questionId: question.id,
    focusAnswerId: answer.id,
  });

  const {isLiked, likedCount, handleLiked} = useAnswerLiked({
    questionId: question.id,
    answerId: answer.id,
    adviserName: answer.adviser.userName,
    isLikedByAPI: answer.liked,
    likedCountByAPI: answer.likeCount,
  });

  return (
    <div className="mb-16 bg-white shadow-card">
      <h2 className="px-16 pt-16 text font-bold leading text-black-1">
        <CommonLink
          href={QuestionDetailURL}
          className={
            'block pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
          }
        >
          {convertNlToBr(question.body)}
        </CommonLink>
      </h2>
      <div className="px-16 pb-16">
        <CommonLink
          href={createBoxPath({
            page: BOX_PAGE_ADVISER_DETAIL,
            adviserName: answer.adviser.userName,
          })}
          className={
            'mt-8 flex w-full pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
          }
        >
          <div className="h-[40px] w-[40px] overflow-hidden rounded-full">
            <Image
              src={
                answer.adviser.iconImageURL
                  ? answer.adviser.iconImageURL
                  : '/next-image/box/default.svg'
              }
              width={40}
              height={40}
              alt={
                answer.adviser.displayName
                  ? answer.adviser.displayName
                  : '相談室回答者'
              }
              style={{objectFit: 'cover'}}
              unoptimized={true}
            />
          </div>
          <div className="w-[calc(100%_-_40px)] pl-8">
            <p className="text leading text-link-1">
              {answer.adviser.displayName
                ? answer.adviser.displayName
                : '相談室回答者'}
            </p>
            <div className="overflow-hidden">
              <p className="line-clamp-1 text-xs leading text-black-3">
                {answer.adviser.introduction}
              </p>
            </div>
          </div>
        </CommonLink>
        <div className="mt-8 max-h-[70px] bg-background-1 p-12">
          <CommonLink
            href={QuestionDetailURL}
            className={
              ' line-clamp-3 text-xs text-black-1 pc:line-clamp-2 pc:text pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
            }
          >
            {answer.body}
          </CommonLink>
        </div>
        <div className="mt-[10px] flex justify-between">
          <p className="flex items-center justify-end pt-4 text-xs leading-sm text-black-4">
            <span>回答日：{formatDate(answer.answeredAt)}</span>
          </p>
          <div className="flex justify-end">
            <LikeButton
              isLiked={isLiked}
              likedCount={likedCount}
              handleLiked={async () => handleLiked()}
            />
            <ShareButton className="ml-[22px]" path={QuestionDetailURL} />
          </div>
        </div>
      </div>

      {question.answerCount > 1 && (
        <div className="flex justify-center border-t-[1px] border-line-1 py-16">
          <CommonLink
            href={createBoxPath({
              page: BOX_PAGE_QUESTION_DETAIL,
              questionId: question.id,
            })}
            className={
              'text leading text-link-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
            }
          >
            全ての回答を見る
            <span className="pl-4 text-xs">(全{question.answerCount}件)</span>
          </CommonLink>
        </div>
      )}
    </div>
  );
};
